// src/pages/wallet/LaboratoryPharmacyDebitsPage.js

import { Grid, Paper, Button, Typography } from "@material-ui/core";
import PageTitle from "components/PageTitle/PageTitle";
import { useState, useCallback, useMemo } from "react";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";

import useStyles from "./styles";
import PharmacySelector from "components/CustomFields/PharmacySelector";
import PharmacyDebitsMuiDataTable from "components/MUIDatatable/PharmacyDebitsTable";
import DateField from "components/CustomFields/DateField";
import { getCurrentDate } from "utils/dateGetters.js";
import DistributorsField from "components/CustomFields/DistributorsField";
import RefreshIcon from "@material-ui/icons/Refresh";
import DoneIcon from "@material-ui/icons/Done";
import WarningIcon from "@material-ui/icons/Warning";

export default function LaboratoryPharmacyDebitsPage() {
  const classes = useStyles();

  // Filtro actual en edición
  const [selectedRut, setSelectedRut] = useState("");
  const [period, setPeriod] = useState(null);
  const [selectedDistributor, setSelectedDistributor] = useState("");
  // Filtro aplicado (lo que ve la grilla)
  const [applyClicked, setApplyClicked] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  // Sentinelas para forzar estado "dirty" al inicio
  const INIT = "__INIT__";
  const [lastApplied, setLastApplied] = useState({
    rut: INIT,
    period: INIT,          // YYYY-MM cuando se aplique por primera vez
    distributor: INIT,
  });


  const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();


  // "YYYY-MM" desde Date o string
  const normPeriodStr = (d) =>
    !d ? null : (typeof d === "string" ? d.slice(0, 7) : new Date(d).toISOString().slice(0, 7));

  // "AAAAMM" (int) desde Date|string|null
  const toAaaammInt = (d) => {
    const yyyymm = normPeriodStr(d); // "YYYY-MM" | null
    if (!yyyymm) return null;
    const [y, m] = yyyymm.split("-");
    return Number(`${y}${m}`);
  };

  const normPeriod = (d) =>
    !d ? null : (typeof d === "string" ? d.slice(0, 7) : new Date(d).toISOString().slice(0, 7));

  const isDirty = useMemo(() => {
    return (
      (selectedRut || null) !== (lastApplied.rut || null) ||
      (String(selectedDistributor || "") || null) !== (String(lastApplied.distributor || "") || null) ||
      (normPeriodStr(period) || null) !== (lastApplied.period || null)
    );
  }, [selectedRut, selectedDistributor, period, lastApplied]);

  const onApply = () => {
    setApplyClicked(true);
    setReloadToken(t => t + 1);   // fuerza remount del grid

    setLastApplied({
      rut: selectedRut || null,
      distributor: selectedDistributor || null,       // string (lo convertimos al pasar al grid)
      period: normPeriodStr(period) || null,          // "YYYY-MM"
    });

  };

  const handleRutChange = (rut) => {
    setSelectedRut(rut || "");
  };

  const handlePeriodChange = (date) => {
    setPeriod(date);
  };

  const onChangeDistributor = (e) => {
    setSelectedDistributor(e.target.value || "");
  };


  return (
    <Grid>
      <CustomizedSnackbars
        severity={snackbarStatus.severity}
        mssg={snackbarStatus.message}
        open={snackbarStatus.open}
        onClose={onCloseSnackbar}
      />

      <PageTitle title="Billetera virtual - Cuentas Corrientes" />

      <Paper elevation={3} className={classes.paperContainer}>
        <Grid container direction="column" style={{ padding: 16, background: "#f5f5f5" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Typography className={classes.sectionTitle}>Filtros</Typography>
          </div>

          <div className={classes.filterContainer}>
            {/* Período */}
            <Grid container className={classes.filterRow}>
              <Grid item className={classes.labelCol}>
                <Typography>Período</Typography>
              </Grid>
              <Grid item className={classes.fieldCol}>
                <DateField
                  views={["year", "month"]}
                  minDate={new Date("2013-10-01")}
                  maxDate={getCurrentDate()}
                  format={"MM/yyyy"}
                  value={period}
                  placeholder="Seleccione Periodo"
                  onChange={handlePeriodChange}
                  fieldClassName={`${classes.dateSelectionField} ${classes.fieldFullWidth}`}
                  clearable
                  clearTooltip="Quitar período"
                />
              </Grid>
            </Grid>

            {/* Distribuidor */}
            <Grid container className={classes.filterRow}>
              <Grid item className={classes.labelCol}>
                <Typography>Distribuidor</Typography>
              </Grid>
              <Grid item className={classes.fieldCol}>
                <DistributorsField
                  fieldClassName={`${classes.generalSelectionField} ${classes.fieldFullWidth}`}
                  value={selectedDistributor}
                  program={1}
                  onChange={onChangeDistributor}
                  only_with_wallet={true}
                  clearable
                  clearText="— Quitar distribuidor —"
                />
              </Grid>
            </Grid>

            {/* Farmacia */}
            <Grid container className={classes.filterRow}>
              <Grid item className={classes.labelCol}>
                <Typography>Farmacia</Typography>
              </Grid>
              <Grid item className={classes.fieldCol}>
                <PharmacySelector
                  fieldClassName={`${classes.pharmacySelectionField} ${classes.fieldFullWidth}`}
                  value={selectedRut}
                  onChange={handleRutChange}
                  program={1}
                  only_with_wallet={true}
                />
              </Grid>
            </Grid>

            {/* Botón Aplicar */}
            <Grid container justify="center" style={{ marginTop: 16 }}>
              <Button

                size="large"
                style={{ fontWeight: 700, borderRadius: 8, textTransform: "none", padding: "8px 24px" }}
                onClick={onApply}
                variant={"contained"}
                color={isDirty ? "secondary" : "default"}
                startIcon={isDirty ? <RefreshIcon /> : <DoneIcon />}
              >
                Aplicar
              </Button>
            </Grid>
          </div>


          {/* Grilla */}
          {applyClicked && (
            <Grid item xs={12} style={{ marginTop: 16, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <PharmacyDebitsMuiDataTable
                key={reloadToken}
                pharmacyRut={lastApplied.rut === "__INIT__" ? null : (lastApplied.rut || null)}
                period={toAaaammInt(lastApplied.period)}                         // int AAAAMM | null
                distributorId={
                  lastApplied.distributor && lastApplied.distributor !== "__INIT__"
                    ? Number(lastApplied.distributor)
                    : null
                }                                                                 // int | null
              />
            </Grid>
          )}
        </Grid>
      </Paper>

    </Grid>
  );
}
